import { useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNumber } from 'lodash';
import {
  G600,
  W400,
  Table,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  UniqkeyAccessScoreIcon,
  SecurityScoreIcon,
  TypographyWithTooltip,
  IMaterialTableOptions,
  IMaterialTableProps,
  Typography,
  Tooltip,
  getTimeFromNow,
} from '@uniqkey-frontend/shared-app';
import {
  EmployeeAccountStatus,
  EmployeeAccountType,
  GetEmployeeAccountsV2ResponseModel,
} from '@uniqkey-backend-organization-web/api-client';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import {
  EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS,
  SCIM_TRANSLATION_KEYS,
  EMPLOYEE_ACCOUNT_TYPE_TRANSLATION_KEYS,
  EMPLOYEE_ACCOUNT_STATUS_COLORS,
  SCIM_COLORS,
} from '../../../constants';
import { TGetEmployeeAccountsMethod } from '../../../hooks/useEmployeeAccountsAPI/interfaces';
import { canHaveSecurityScore, parseSecurityScore } from '../../../helpers/employee';
import UniqkeyAccessScoreStatisticsTooltipContent
  from './components/UniqkeyAccessScoreStatisticsTooltipContent';
// import OnboardingProgressBar
// from '../../../pages/EmployeesPage/components/OnboardingProgressBar';

export interface IEmployeesTableRow extends GetEmployeeAccountsV2ResponseModel {
  progress: number;
  lastActivity: string | null;
}

interface IEmployeesTableProps extends
  Omit<IUseTableReturn<IEmployeesTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetEmployeeAccountsMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  employees: IUseQueryTableFetchReturn<TGetEmployeeAccountsMethod>['data'];
  selectedEmployees: IUseTableReturn<IEmployeesTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  isSecretTokenEmpty: boolean;
}

const TABLE_OPTIONS: IMaterialTableOptions<IEmployeesTableRow> = {
  selection: true,
};

const SHOW_ROLE_COLUMN = {
  [EmployeeAccountStatus.Active]: true,
  [EmployeeAccountStatus.Invited]: true,
  [EmployeeAccountStatus.Archived]: true,
  [EmployeeAccountStatus.Unprocessed]: false,
  [EmployeeAccountStatus.Staged]: true,
  [EmployeeAccountStatus.MigrationInvited]: true,
  [EmployeeAccountStatus.Migrated]: true,
  [EmployeeAccountStatus.ExistingUnprocessed]: false,
};

const TOOLTIP_PROPS = { cursorPointer: true };

const EmployeesTable = (props: IEmployeesTableProps) => {
  const {
    employees: preEmployees,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedEmployees,
    onRowSelectionChange,
    createColumns,
    isSecretTokenEmpty,
  } = props;

  const { t, currentLanguage } = useTranslations();
  const navigate = useNavigate();

  const employees = useMemo(() => preEmployees.map((employee) => ({
    ...employee,
    lastActivity: getTimeFromNow({
      date: employee.activityAt,
      locale: currentLanguage,
    }),
    progress: 0, // TODO: replace with real data
  })), [preEmployees, currentLanguage]);

  const columns = useMemo(() => createColumns([
    {
      title: t('employeesPage.status'),
      field: 'employeeAccountStatus',
      render: (rowData) => {
        if (rowData.employeeAccountStatus === EmployeeAccountStatus.Unprocessed) {
          return (
            <TypographyWithTooltip
              variant="body2"
              color={EMPLOYEE_ACCOUNT_STATUS_COLORS[rowData.employeeAccountStatus]}
              title={t('common.openMobileApp')}
            >
              {t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[rowData.employeeAccountStatus])}
            </TypographyWithTooltip>
          );
        }
        if (rowData.employeeAccountStatus === EmployeeAccountStatus.ExistingUnprocessed) {
          return (
            <TypographyWithTooltip
              variant="body2"
              color={EMPLOYEE_ACCOUNT_STATUS_COLORS[rowData.employeeAccountStatus]}
              title={t('employeesPage.existingUnprocessedTooltip')}
            >
              {t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[rowData.employeeAccountStatus])}
            </TypographyWithTooltip>
          );
        }
        return (
          <Typography
            variant="body2"
            color={EMPLOYEE_ACCOUNT_STATUS_COLORS[rowData.employeeAccountStatus]}
          >
            {t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[rowData.employeeAccountStatus])}
          </Typography>
        );
      },
      width: '15%',
    },
    {
      title: t('employeesPage.email'),
      field: 'email',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.email}
        </TypographyWithTooltip>
      ),
      width: '20%',
    },
    {
      title: t('employeesPage.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('employeesPage.role'),
      field: 'employeeAccountType',
      render: (rowData) => SHOW_ROLE_COLUMN[rowData.employeeAccountStatus] && (
        <TypographyWithTooltip
          variant="body2"
          color={rowData.employeeAccountType === EmployeeAccountType.KeylessAdmin ? W400 : G600}
        >
          {t(EMPLOYEE_ACCOUNT_TYPE_TRANSLATION_KEYS[rowData.employeeAccountType])}
        </TypographyWithTooltip>
      ),
      width: '10%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('employeesPage.securityScore.columnTooltip')}
        >
          {t('employeesPage.securityScore')}
        </TypographyWithTooltip>
      ),
      field: 'securityScore',
      render: (rowData) => (
        canHaveSecurityScore(rowData.employeeAccountStatus) && isNumber(rowData.securityScore)
          ? <SecurityScoreIcon percentage={parseSecurityScore(rowData.securityScore)} />
          : t('common.noScore')
      ),
      width: '10%',
    },
    {
      title: t('employeesPage.uniqkeyAccessScore'),
      field: 'uniqkeyAccessScore',
      render: (rowData) => (isNumber(rowData.uniqkeyAccessScore)
        ? (
          <Tooltip
            cursorPointer
            title={(
              <UniqkeyAccessScoreStatisticsTooltipContent
                masterPasswordStrength={rowData.masterPasswordStrength ?? 0}
                isMasterPasswordReused={rowData.isMasterPasswordReused}
                hasReusedPasswords={rowData.hasReusedPasswords}
              />
            )}
          >
            <UniqkeyAccessScoreIcon percentage={rowData.uniqkeyAccessScore} />
          </Tooltip>
        )
        : t('common.noScore')),
      width: '10%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('employeesPage.scim.columnTooltip')}
        >
          {t(SCIM_TRANSLATION_KEYS.true)}
        </TypographyWithTooltip>
      ),
      field: 'isByScim',
      render: (rowData) => (
        <Typography
          variant="body2"
          color={isSecretTokenEmpty
            ? SCIM_COLORS.false
            : SCIM_COLORS[rowData.isByScim.toString() as keyof typeof SCIM_COLORS]}
        >
          {isSecretTokenEmpty ? t('scim.no') : t(SCIM_TRANSLATION_KEYS[
            rowData.isByScim.toString() as keyof typeof SCIM_TRANSLATION_KEYS
          ])}
        </Typography>
      ),
      width: '8%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('employeesPage.lastActivity.columnTooltip')}
        >
          {t('employeesPage.lastActivity')}
        </TypographyWithTooltip>
      ),
      field: 'activityAt',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.lastActivity}
        </TypographyWithTooltip>
      ),
      width: '12%',
    },
    /* {
      // original width
      // field: 'employeeAccountStatus', width: '15%',
      // field: 'email', width: '30%',
      // field: 'employeeAccountType', width: '15%',
      // field: 'securityScore', width: '15%',
      // field: 'progress', width: '25%',

      title: t('employeesPage.onboardingProgress'),
      field: 'progress',
      render: (rowData) => (
        <OnboardingProgressBar progress={rowData.progress} />
      ),
      sorting: false, // TODO: remove when you'll have real data
      width: '25%',
    }, */
  ]), [createColumns, isSecretTokenEmpty, t]);

  const handleEmployeeRowClick = useCallback<
    NonNullable<IMaterialTableProps<IEmployeesTableRow>['onRowClick']>
  >((event, employee) => {
    const { employeeAccountStatus, employeeAccountId } = employee!;
    if (employeeAccountStatus === EmployeeAccountStatus.Unprocessed
      || employeeAccountStatus === EmployeeAccountStatus.ExistingUnprocessed) {
      return;
    }
    navigate(`${PageRouteEnum.Employees}/${employeeAccountId}`);
  }, [navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={employees}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedEmployees}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleEmployeeRowClick}
      rowSelectByKey="employeeAccountId"
      rowHeight={51}
    />
  );
};

export default memo(EmployeesTable);
